/**
 * Centralized authorization helpers.
 *
 * Pure functions that take user/role data and return booleans.
 * Use these in components instead of inline checks so permission
 * logic lives in one place.
 *
 * For reactive (hook-based) checks that automatically re-render
 * when permissions change, see hooks/usePermissions.ts.
 */

/** User shape required for global permission checks. */
interface GlobalUser {
  isManager: boolean;
  isAdmin: boolean;
}

/**
 * Can the user create or edit programs?
 * Requires manager flag OR cohort-manager-admin POSIX group membership.
 */
export function canCreateProgram(user: GlobalUser): boolean {
  return user.isManager || user.isAdmin;
}

/**
 * Can the user manage a specific program (edit, add cohorts, manage admins)?
 * Requires being a program admin for that program.
 */
export function canManageProgram(isProgramAdmin: boolean): boolean {
  return isProgramAdmin;
}

/**
 * Can the user create or edit cohorts within a program?
 * Requires being a program admin for that program.
 */
export function canManageCohort(isProgramAdmin: boolean): boolean {
  return isProgramAdmin;
}

/**
 * Can the user manage enrollments (enroll/unenroll participants)?
 * Requires being a program admin for the cohort's program.
 */
export function canManageEnrollments(isProgramAdmin: boolean): boolean {
  return isProgramAdmin;
}

/**
 * Can the user manage mentors (add/remove/assign)?
 * Requires being a program admin for the cohort's program.
 */
export function canManageMentors(isProgramAdmin: boolean): boolean {
  return isProgramAdmin;
}

/**
 * Can the user review graduation requests?
 * Requires being a program admin for the cohort's program.
 */
export function canReviewGraduation(isProgramAdmin: boolean): boolean {
  return isProgramAdmin;
}

/**
 * Can the user approve activity completions?
 * Requires being a program admin OR an active mentor in the cohort.
 */
export function canApproveActivities(isProgramAdmin: boolean, isMentor: boolean): boolean {
  return isProgramAdmin || isMentor;
}
